import { useState, useMemo, useContext } from 'react';
import { useListToolCatalog } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  useOTPTargetsList,
  useSmsNumber,
} from 'src/api/rpc/hooks/otp/useOTPTargets';
import { useCredentialsList } from 'src/api/rpc/hooks/credentials/useCredentials';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { Badge } from 'src/components/ui/badge';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import {
  CaretLeftIcon,
  CircleNotchIcon,
  EyeIcon,
  GearSixIcon,
  HammerIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ProhibitIcon,
  RobotIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Button } from 'src/components/ui/button';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { cn } from 'src/lib/utils';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';
import { MarkdownWYSIWYGEditor } from 'src/components/common/MarkdownWYSIWYGEditor';
import { useAgentDefinitionForm } from './AgentDefinitionFormContext';
import type { AgentDefinitionResponse, CatalogToolMetadata } from '@erp/rpc';
import {
  CASH_RECEIPT_KEYING_CONFIG_KEY,
  OPERATOR_AGENT_CARD,
  cashReceiptKeyingConfigSchema,
  type CashReceiptKeyingConfig,
  type OperatorModelTier,
} from '@erp/shared-types';
import {
  getRemoteDesktopConfig,
  OPERATOR_MODEL_VALUES_SET,
  OPERATOR_MODEL_TIERS,
  type RemoteDesktopConfig,
  type EmailModelTier,
} from './components/remote-desktop-config';
import { RemoteDesktopLoginConfigFields } from './components/RemoteDesktopLoginConfigFields';
import { RemoteDesktopExecuteConfigFields } from './components/RemoteDesktopExecuteConfigFields';
import { CashReceiptKeyingConfigFields } from './components/CashReceiptKeyingConfigFields';
import { Plural, T, Var, useGT, useMessages } from 'gt-react';
import { PermissionListPopover } from 'src/components/permissions/PermissionListPopover';
import { PasswordCredentialOptions } from 'src/components/common/PasswordCredentialOptions';
import { WorkflowActionToolsSection } from './components/WorkflowActionToolsSection';
import { WorkbookActionToolsSection } from './components/WorkbookActionToolsSection';

type WebBrowserModelTier = OperatorModelTier;

type WebBrowserCredentialEntry = {
  credentialId: string;
  label: string;
  draftId?: string;
};

type WebBrowserConfig = {
  model: WebBrowserModelTier;
  credentials: WebBrowserCredentialEntry[];
  headless: boolean;
  // Step-parity knobs (the agent tool replaces the web_browser workflow STEP for
  // queued portal fetches — see webBrowserConfigSchema in webBrowserExecute.ts).
  // Empty string / null mean "unset": the key is removed on save so the backend
  // schema's url/number validators never see an empty value.
  startUrl: string;
  operatorInstructions: string;
  otpTargetId: string;
  timeoutSeconds: number | null;
  typingDelayMs: number;
  mouseSmooth: boolean;
  mouseDurationMs: number | null;
  // Legacy per-tool procedure allowlist. The Procedures tab now owns this as the unified
  // config.procedures.sopIds (see procedureConfig.ts); we only read+preserve it here so a
  // Tools-tab save on an un-migrated agent doesn't drop it before that migration.
  // ponytail: delete this field once every agent config carries config.procedures.
  sopIds: string[];
};

const DEFAULT_WEB_BROWSER_CONFIG: WebBrowserConfig = {
  model: 'gpt',
  credentials: [],
  headless: false,
  startUrl: '',
  operatorInstructions: '',
  otpTargetId: '',
  timeoutSeconds: null,
  typingDelayMs: 0,
  mouseSmooth: false,
  mouseDurationMs: null,
  sopIds: [],
};

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function toCredentialEntries(value: unknown): WebBrowserCredentialEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry): entry is { credentialId: string; label: string } =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as Record<string, unknown>).credentialId === 'string' &&
        typeof (entry as Record<string, unknown>).label === 'string',
    )
    .map((entry) => ({ ...entry, draftId: crypto.randomUUID() }));
}

function toSopIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string');
}

function getWebBrowserConfig(
  config: AgentDefinitionResponse['config'],
): WebBrowserConfig {
  const raw =
    config && typeof config.webBrowser === 'object' && config.webBrowser
      ? (config.webBrowser as Record<string, unknown>)
      : {};

  return {
    model: OPERATOR_MODEL_VALUES_SET.has(raw.model as OperatorModelTier)
      ? (raw.model as WebBrowserModelTier)
      : DEFAULT_WEB_BROWSER_CONFIG.model,
    credentials: toCredentialEntries(raw.credentials),
    headless: toBoolean(raw.headless, DEFAULT_WEB_BROWSER_CONFIG.headless),
    startUrl: typeof raw.startUrl === 'string' ? raw.startUrl : '',
    operatorInstructions:
      typeof raw.operatorInstructions === 'string'
        ? raw.operatorInstructions
        : '',
    otpTargetId: typeof raw.otpTargetId === 'string' ? raw.otpTargetId : '',
    timeoutSeconds:
      typeof raw.timeoutSeconds === 'number' ? raw.timeoutSeconds : null,
    typingDelayMs:
      typeof raw.typingDelayMs === 'number' ? raw.typingDelayMs : 0,
    mouseSmooth: toBoolean(raw.mouseSmooth, false),
    mouseDurationMs:
      typeof raw.mouseDurationMs === 'number' ? raw.mouseDurationMs : null,
    // Preserved, not edited here — see the field comment on WebBrowserConfig.
    sopIds: toSopIds(raw.sopIds),
  };
}

/** The agent's cash-receipt keying settings; an unreadable value shows the defaults. */
function getCashReceiptKeyingConfig(
  config: AgentDefinitionResponse['config'],
): CashReceiptKeyingConfig {
  const parsed = cashReceiptKeyingConfigSchema.safeParse(
    config?.[CASH_RECEIPT_KEYING_CONFIG_KEY] ?? {},
  );
  return parsed.success ? parsed.data : cashReceiptKeyingConfigSchema.parse({});
}

function isTaskManagementEnabled(
  config: Record<string, unknown> | null,
): boolean {
  const tm =
    config && typeof config.taskManagement === 'object' && config.taskManagement
      ? (config.taskManagement as Record<string, unknown>)
      : undefined;
  return tm?.enabled === true;
}

// A tool routed through an A2A skill runs as a delegated sub-agent. Pure and
// state-free, so it lives at module scope (rebuilding it per render would waste
// work and break memoized children).
function isSubAgentTool(toolType: string): boolean {
  return toolType === 'web_browser_execute';
}

export function AgentDefinitionToolsTab() {
  const gt = useGT();
  const m = useMessages();
  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId;
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  const {
    definition,
    currentVersion,
    agentForm,
    enabledTools,
    baseTools,
    setLocalTools,
    localConfig,
    baseConfig,
    setLocalConfig,
    isEditable,
  } = useAgentDefinitionForm();

  // When task management is on, the loop owns the task lifecycle, so
  // lifecycle-managed tools (update_task_status, reassign_task) are stripped at
  // runtime and disabled here. The `lifecycleManaged` flag comes from the tool
  // catalog so the UI and backend agree on which tools those are. Task
  // management lives on `agentForm.config` (what TaskManagementSection edits) —
  // fall back to the saved definition config when the form isn't seeded yet.
  // `localConfig` is the per-tool config override and never holds it.
  const taskManagementEnabled = isTaskManagementEnabled(
    agentForm?.config ?? baseConfig,
  );

  const { data: catalogData, isLoading: catalogLoading } = useListToolCatalog();
  const { configs: otpConfigs } = useOTPTargetsList();
  const { data: smsNumber } = useSmsNumber();
  const { credentials: passwordCredentials } = useCredentialsList({
    organizationId,
    type: 'password',
  });

  const [search, setSearch] = useState('');
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [activeToolType, setActiveToolType] = useState<string | null>(null);

  const originalTools = useMemo(
    () => baseTools.map((tool) => tool.toolType),
    [baseTools],
  );
  const originalConfig = baseConfig;
  const currentConfig = localConfig ?? originalConfig;

  const deprecatedCount = useMemo(
    () => (catalogData?.tools ?? []).filter((t) => t.deprecated).length,
    [catalogData?.tools],
  );

  // Group tools by category
  const groupedTools = useMemo(() => {
    const tools = catalogData?.tools ?? [];
    const searchLower = search.toLowerCase();
    const filtered = tools.filter(
      (t) =>
        // Resource-lifecycle tools (open/close browser) are invoked by the task
        // loop, never granted to an agent — keep them out of the grantable catalog.
        t.agentGrantable !== false &&
        (showDeprecated || !t.deprecated) &&
        (t.name.toLowerCase().includes(searchLower) ||
          t.description.toLowerCase().includes(searchLower)),
    );

    const groups = new Map<string, CatalogToolMetadata[]>();
    for (const tool of filtered) {
      const group = groups.get(tool.category) ?? [];
      group.push(tool);
      groups.set(tool.category, group);
    }
    return groups;
  }, [catalogData?.tools, search, showDeprecated]);

  if (!definition || catalogLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  function toggleTool(toolType: string) {
    if (!isEditable) return;
    setLocalTools((prev) => {
      const current = prev ?? originalTools;
      if (current.includes(toolType)) {
        return current.filter((t) => t !== toolType);
      }
      return [...current, toolType];
    });
  }

  function updateConfig(
    updater: (
      current: AgentDefinitionResponse['config'],
    ) => AgentDefinitionResponse['config'],
  ) {
    if (!isEditable) return;
    setLocalConfig((prev) => updater(prev ?? originalConfig));
  }

  function setRemoteDesktopConfig(
    updater: (current: RemoteDesktopConfig) => RemoteDesktopConfig,
  ) {
    updateConfig((config) => {
      const currentRemoteDesktopConfig = getRemoteDesktopConfig(config);
      const nextRemoteDesktopConfig = updater(currentRemoteDesktopConfig);
      return {
        ...config,
        ...nextRemoteDesktopConfig,
      };
    });
  }

  function setWebBrowserConfig(
    updater: (current: WebBrowserConfig) => WebBrowserConfig,
  ) {
    updateConfig((config) => {
      const raw =
        config && typeof config.webBrowser === 'object' && config.webBrowser
          ? (config.webBrowser as Record<string, unknown>)
          : {};
      const next = updater(getWebBrowserConfig(config));
      // Spread the raw object first so knobs this tab has no fields for
      // (credentialSetId/autoLogin, remoteDesktopLogin, healMode,
      // snapCursorBeforeClick, ...) survive a save instead of being clobbered
      // by the fixed field set below.
      const merged: Record<string, unknown> = {
        ...raw,
        model: next.model,
        credentials: next.credentials,
        headless: next.headless,
        typingDelayMs: next.typingDelayMs,
        mouseSmooth: next.mouseSmooth,
        sopIds: next.sopIds,
      };
      // Optional knobs: empty means "unset" — remove the key so the backend
      // schema's url/number validators never see an empty value and stored
      // configs stay sparse.
      const optionals: Array<[key: string, value: unknown, unset: boolean]> = [
        ['startUrl', next.startUrl, next.startUrl === ''],
        [
          'operatorInstructions',
          next.operatorInstructions,
          next.operatorInstructions === '',
        ],
        ['otpTargetId', next.otpTargetId, next.otpTargetId === ''],
        ['timeoutSeconds', next.timeoutSeconds, next.timeoutSeconds === null],
        [
          'mouseDurationMs',
          next.mouseDurationMs,
          next.mouseDurationMs === null,
        ],
      ];
      for (const [key, value, unset] of optionals) {
        if (unset) {
          delete merged[key];
        } else {
          merged[key] = value;
        }
      }
      return { ...config, webBrowser: merged };
    });
  }

  function parseEmailList(input: string): string[] {
    return input
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }

  const remoteDesktopConfig = getRemoteDesktopConfig(currentConfig);
  const webBrowserConfig = getWebBrowserConfig(currentConfig);
  const catalogByType = new Map(
    (catalogData?.tools ?? []).map((tool) => [tool.type, tool] as const),
  );

  // Split the enabled list into sub-agents vs plain tools so the distinction is
  // explicit (isSubAgentTool is hoisted to module scope).
  const subAgentTools = enabledTools.filter(isSubAgentTool);
  const regularTools = enabledTools.filter((t) => !isSubAgentTool(t));

  const selectedCatalogTool = activeToolType
    ? catalogByType.get(activeToolType)
    : undefined;
  const selectedTool =
    selectedCatalogTool ||
    (activeToolType
      ? {
          type: activeToolType,
          name: activeToolType,
          description: '',
          readOnly: false,
          requiredPermissions: [],
        }
      : null);
  const selectedToolIsEnabled =
    !!selectedTool && enabledTools.includes(selectedTool.type);
  const selectedToolPermissionCount =
    selectedTool?.requiredPermissions?.length ?? 0;

  function renderEnabledToolRow(toolType: string) {
    const tool = catalogByType.get(toolType);
    const permCount = tool?.requiredPermissions?.length ?? 0;
    const isA2AOperator = isSubAgentTool(toolType);
    return (
      <div
        key={toolType}
        className={cn(
          'flex items-start gap-2 rounded-md p-2 cursor-pointer text-sm transition-colors',
          activeToolType === toolType
            ? 'bg-accent text-accent-foreground'
            : 'hover:bg-muted',
        )}
        onClick={() => setActiveToolType(toolType)}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isA2AOperator && (
              <RobotIcon className="h-3.5 w-3.5 text-indigo-600 flex-shrink-0" />
            )}
            <span
              className={cn(
                'truncate font-medium',
                tool?.deprecated && 'line-through text-muted-foreground',
              )}
            >
              {tool?.name ?? toolType}
            </span>
            {tool?.deprecated && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1 py-0 text-muted-foreground flex-shrink-0"
                  >
                    <T context="badge for deprecated agent tool in enabled list">
                      Deprecated
                    </T>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{tool.deprecated.reason}</TooltipContent>
              </Tooltip>
            )}
            {tool?.readOnly && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <EyeIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent>
                  <T context="tooltip agent tool is read-only">Read-only</T>
                </TooltipContent>
              </Tooltip>
            )}
            {permCount > 0 && (
              <PermissionListPopover
                permissions={tool?.requiredPermissions ?? []}
              >
                <span className="inline-flex items-center gap-0.5 text-muted-foreground flex-shrink-0 cursor-pointer">
                  <ShieldCheckIcon className="h-3.5 w-3.5" />
                  <span className="text-[10px]">{permCount}</span>
                </span>
              </PermissionListPopover>
            )}
          </div>
          {tool?.description && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {tool.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    // min-w-0 because a fieldset's UA min-inline-size is min-content, which would otherwise stop
    // this flex row from shrinking and overflow the page on narrow viewports.
    <fieldset
      disabled={!isEditable}
      // Fixed to roughly the viewport so long tool lists scroll inside their panels instead of
      // pushing the workflow/workbook action sections below the fold.
      className="flex h-[calc(100dvh-14rem)] min-h-[500px] w-full min-w-0 rounded-lg border disabled:opacity-70"
    >
      {/* Left panel: Enabled tools */}
      <div className="w-64 flex-shrink-0 border-r flex flex-col min-h-0">
        <div className="flex items-center justify-between p-3 border-b flex-shrink-0">
          <h3 className="text-sm font-semibold">
            <T context="agent definition tools tab left panel title">
              Enabled Tools
            </T>
          </h3>
          <Badge variant="secondary" className="text-xs">
            {enabledTools.length}
          </Badge>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-1.5 space-y-0.5">
          {enabledTools.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8 px-3">
              <T context="empty state when no agent tools are enabled">
                No tools enabled. Browse the catalog to add tools.
              </T>
            </p>
          ) : subAgentTools.length > 0 ? (
            // Group the list so the tool/sub-agent split is explicit.
            <>
              <p className="px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <T context="section header grouping delegated A2A sub-agents in the enabled tools list">
                  Sub-agents
                </T>
              </p>
              {subAgentTools.map(renderEnabledToolRow)}
              {regularTools.length > 0 && (
                <>
                  <p className="mt-2 border-t px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <T context="section header grouping plain tools in the enabled tools list">
                      Tools
                    </T>
                  </p>
                  {regularTools.map(renderEnabledToolRow)}
                </>
              )}
            </>
          ) : (
            enabledTools.map(renderEnabledToolRow)
          )}
        </div>

        <div className="max-h-[45%] flex-shrink-0 overflow-y-auto border-t">
          <WorkflowActionToolsSection
            config={currentConfig}
            onChange={(next) => updateConfig(() => next)}
          />
          <WorkbookActionToolsSection
            config={currentConfig}
            onChange={(next) => updateConfig(() => next)}
          />
        </div>
      </div>

      {/* Right panel: Config or Catalog */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeToolType && selectedTool ? (
          <>
            {/* Config header */}
            <div className="flex items-center gap-2 p-2 border-b flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={() => setActiveToolType(null)}
              >
                <CaretLeftIcon className="h-4 w-4" />
              </Button>
              <GearSixIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium truncate">
                {selectedTool.name}
              </span>
              {selectedTool.readOnly && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <EyeIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <T context="tooltip selected agent tool is read-only">
                      Read-only
                    </T>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Tool meta */}
            <div className="px-3 py-2 border-b flex-shrink-0">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{selectedTool.description}</span>
                {selectedToolPermissionCount > 0 && (
                  <PermissionListPopover
                    permissions={selectedTool.requiredPermissions ?? []}
                  >
                    <Badge variant="outline" className="text-xs cursor-pointer">
                      <T context="badge count of required permissions for agent tool">
                        <Var>{selectedToolPermissionCount}</Var>{' '}
                        <Plural
                          n={selectedToolPermissionCount}
                          one="permission"
                          other="permissions"
                        />
                      </T>
                    </Badge>
                  </PermissionListPopover>
                )}
              </div>
            </div>

            {/* Enable / Disable banner */}
            {!selectedToolIsEnabled ? (
              <div className="mx-3 mt-3 flex items-center gap-2 rounded-md border border-dashed p-2">
                <p className="text-sm text-muted-foreground flex-1">
                  <T context="banner when selected tool is not enabled">
                    This tool is not enabled for this agent.
                  </T>
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleTool(selectedTool.type)}
                >
                  <T context="button to enable agent tool">Enable</T>
                </Button>
              </div>
            ) : (
              <div className="mx-3 mt-3 flex items-center gap-2 rounded-md border p-2">
                <p className="text-sm text-muted-foreground flex-1">
                  <T context="banner when selected tool is enabled">
                    This tool is enabled.
                  </T>
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    toggleTool(selectedTool.type);
                    setActiveToolType(null);
                  }}
                >
                  <T context="button to remove agent tool from agent">Remove</T>
                </Button>
              </div>
            )}

            {/* Config content */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-3">
                {selectedTool.type === 'login_to_remote_desktop' && (
                  <RemoteDesktopLoginConfigFields
                    credentials={remoteDesktopConfig.credentials}
                    applicationCredentials={
                      remoteDesktopConfig.applicationCredentials
                    }
                    onCredentialsChange={(updater) =>
                      setRemoteDesktopConfig((current) => ({
                        ...current,
                        credentials: updater(current.credentials),
                      }))
                    }
                    onApplicationCredentialsChange={(updater) =>
                      setRemoteDesktopConfig((current) => ({
                        ...current,
                        applicationCredentials: updater(
                          current.applicationCredentials,
                        ),
                      }))
                    }
                    passwordCredentials={passwordCredentials}
                    otpConfigs={otpConfigs}
                    showAdminFields={userIsAdmin}
                    idSuffix={selectedTool.type}
                  />
                )}

                {selectedTool.type === 'generate_session_summary' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`summarySendEmail-${selectedTool.type}`}
                        checked={remoteDesktopConfig.summary.sendEmail}
                        onCheckedChange={(checked) =>
                          setRemoteDesktopConfig((current) => ({
                            ...current,
                            summary: {
                              ...current.summary,
                              sendEmail: checked === true,
                            },
                          }))
                        }
                      />
                      <Label
                        htmlFor={`summarySendEmail-${selectedTool.type}`}
                        className="cursor-pointer"
                      >
                        <T context="checkbox send session summary email when run completes">
                          Send summary email when run completes
                        </T>
                      </Label>
                    </div>

                    {remoteDesktopConfig.summary.sendEmail && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label>
                            <T context="label for AI email generation model tier">
                              Email Generation Model
                            </T>
                          </Label>
                          <Select
                            value={remoteDesktopConfig.summary.emailModel}
                            onValueChange={(value) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  emailModel: value as EmailModelTier,
                                },
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fast">
                                <T context="email model tier fast">Fast</T>
                              </SelectItem>
                              <SelectItem value="balanced">
                                <T context="email model tier balanced">
                                  Balanced
                                </T>
                              </SelectItem>
                              <SelectItem value="smart">
                                <T context="email model tier smart">Smart</T>
                              </SelectItem>
                              <SelectItem value="reasoning">
                                <T context="email model tier reasoning">
                                  Reasoning
                                </T>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for summary email from address">
                              From Email
                            </T>
                          </Label>
                          <Input
                            type="email"
                            value={remoteDesktopConfig.summary.from}
                            onChange={(e) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  from: e.target.value,
                                },
                              }))
                            }
                            placeholder={gt('ops@your-company.com', {
                              context: 'placeholder example from email address',
                            })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for comma-separated recipient emails">
                              Recipients (comma-separated)
                            </T>
                          </Label>
                          <Input
                            value={remoteDesktopConfig.summary.recipients.join(
                              ', ',
                            )}
                            onChange={(e) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  recipients: parseEmailList(e.target.value),
                                },
                              }))
                            }
                            placeholder={gt(
                              'team@your-company.com, manager@your-company.com',
                              {
                                context:
                                  'placeholder example comma-separated emails',
                              },
                            )}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for comma-separated CC emails">
                              CC (comma-separated)
                            </T>
                          </Label>
                          <Input
                            value={remoteDesktopConfig.summary.cc.join(', ')}
                            onChange={(e) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  cc: parseEmailList(e.target.value),
                                },
                              }))
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for comma-separated BCC emails">
                              BCC (comma-separated)
                            </T>
                          </Label>
                          <Input
                            value={remoteDesktopConfig.summary.bcc.join(', ')}
                            onChange={(e) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  bcc: parseEmailList(e.target.value),
                                },
                              }))
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for optional email subject override">
                              Subject Override (optional)
                            </T>
                          </Label>
                          <Input
                            value={remoteDesktopConfig.summary.subject}
                            onChange={(e) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  subject: e.target.value,
                                },
                              }))
                            }
                            placeholder={gt('Remote Desktop Session Summary', {
                              context:
                                'placeholder default summary email subject',
                            })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for optional summary instructions editor">
                              Summary Instructions (optional)
                            </T>
                          </Label>
                          <MarkdownWYSIWYGEditor
                            key={`${definition.id}-${currentVersion?.id ?? definition.updatedAt}-${selectedTool.type}-summary-instructions`}
                            initialValue={
                              remoteDesktopConfig.summary.summaryInstructions
                            }
                            readonly={!isEditable}
                            onChange={(content) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  summaryInstructions: content,
                                },
                              }))
                            }
                            placeholder={gt(
                              'Describe what the summary email should focus on, e.g. which invoices were purchase matched and which failed',
                              {
                                context:
                                  'placeholder for session summary instructions',
                              },
                            )}
                            height={120}
                            showToolbar
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            <T context="label for optional summary email template example">
                              Template Example (optional)
                            </T>
                          </Label>
                          <MarkdownWYSIWYGEditor
                            key={`${definition.id}-${currentVersion?.id ?? definition.updatedAt}-${selectedTool.type}-template-example`}
                            initialValue={
                              remoteDesktopConfig.summary.templateExample
                            }
                            readonly={!isEditable}
                            onChange={(content) =>
                              setRemoteDesktopConfig((current) => ({
                                ...current,
                                summary: {
                                  ...current.summary,
                                  templateExample: content,
                                },
                              }))
                            }
                            placeholder={gt(
                              'Example wording/tone for summary emails',
                              {
                                context:
                                  'placeholder for summary email template example',
                              },
                            )}
                            height={170}
                            showToolbar
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedTool.type === 'remote_desktop_execute' && (
                  <RemoteDesktopExecuteConfigFields
                    model={remoteDesktopConfig.execute.model}
                    onModelChange={(model) =>
                      setRemoteDesktopConfig((current) => ({
                        ...current,
                        execute: { ...current.execute, model },
                      }))
                    }
                  />
                )}

                {selectedTool.type === 'web_browser_execute' && (
                  <div className="space-y-3">
                    {/* The web browser tool runs as a delegated A2A sub-agent;
                        surface the operator's agent-card identity. */}
                    <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-2.5 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <RobotIcon className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-indigo-900">
                          {OPERATOR_AGENT_CARD.name}
                        </span>
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1 py-0 text-indigo-700 border-indigo-300"
                        >
                          <T context="badge marking the web browser tool as a delegated A2A sub-agent">
                            A2A Agent
                          </T>
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {OPERATOR_AGENT_CARD.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-1">
                        <code className="text-[10px] rounded bg-indigo-100 px-1 py-0.5 text-indigo-700">
                          {OPERATOR_AGENT_CARD.skillId}
                        </code>
                        {OPERATOR_AGENT_CARD.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] rounded-full border border-indigo-200 bg-indigo-100 px-1.5 py-0.5 text-indigo-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>
                        <T context="label for web browser operator computer-use model">
                          Computer-Use Model
                        </T>
                      </Label>
                      <Select
                        value={webBrowserConfig.model}
                        onValueChange={(value) =>
                          setWebBrowserConfig((current) => ({
                            ...current,
                            model: value as WebBrowserModelTier,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATOR_MODEL_TIERS.map((tier) => (
                            <SelectItem key={tier.value} value={tier.value}>
                              {m(tier.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        <T context="help text for web browser computer-use model selector">
                          Model used to drive the computer-use loop. Claude
                          options use Anthropic computer use; GPT uses OpenAI
                          computer use; Gemini uses Google computer use.
                        </T>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="webBrowserStartUrl">
                        <T context="label for web browser start URL">
                          Start URL
                        </T>
                      </Label>
                      <Input
                        id="webBrowserStartUrl"
                        className="h-8 text-xs"
                        value={webBrowserConfig.startUrl}
                        onChange={(e) =>
                          setWebBrowserConfig((current) => ({
                            ...current,
                            startUrl: e.target.value,
                          }))
                        }
                        placeholder={gt('https://example.com', {
                          context:
                            'placeholder for web browser start URL input',
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        <T context="help text for web browser start URL">
                          The portal entry point. When set, the browser always
                          starts here — any URL the agent passes is ignored, so
                          it never has to know (or gets to choose) the address.
                        </T>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="webBrowserOperatorInstructions">
                        <T context="label for pinned operator instructions">
                          Portal instructions (optional)
                        </T>
                      </Label>
                      <Textarea
                        id="webBrowserOperatorInstructions"
                        className="min-h-32 text-xs font-mono"
                        value={webBrowserConfig.operatorInstructions}
                        onChange={(e) =>
                          setWebBrowserConfig((current) => ({
                            ...current,
                            operatorInstructions: e.target.value,
                          }))
                        }
                        placeholder={gt(
                          'Log into the vendor portal with the saved credentials. After the password, the portal asks for a verification code — use the fill_otp_code tool…',
                          {
                            context:
                              'placeholder for pinned operator instructions textarea',
                          },
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        <T context="help text for pinned operator instructions">
                          A navigation script sent to the browser operator
                          verbatim, ahead of whatever the agent asks for — the
                          place for click-by-click portal directions that must
                          not be paraphrased.
                        </T>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>
                          <T context="label for web browser credentials list">
                            Credentials (optional)
                          </T>
                        </Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1 text-xs"
                          onClick={() =>
                            setWebBrowserConfig((current) => ({
                              ...current,
                              credentials: [
                                ...current.credentials,
                                {
                                  credentialId: '',
                                  label: '',
                                  draftId: crypto.randomUUID(),
                                },
                              ],
                            }))
                          }
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                          <T context="button to add a credential entry to web browser tool">
                            Add
                          </T>
                        </Button>
                      </div>

                      {webBrowserConfig.credentials.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          <T context="empty state when no credentials added to web browser tool">
                            No credentials added. Click Add to specify saved
                            credentials for websites the agent needs to log
                            into.
                          </T>
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {webBrowserConfig.credentials.map((entry, idx) => (
                            <div
                              key={entry.draftId ?? idx}
                              className="flex items-start gap-2 rounded-md border p-2"
                            >
                              <div className="flex-1 space-y-1.5 min-w-0">
                                <Select
                                  value={entry.credentialId || '__none__'}
                                  onValueChange={(value) =>
                                    setWebBrowserConfig((current) => {
                                      const next = [...current.credentials];
                                      next[idx] = {
                                        ...next[idx],
                                        credentialId:
                                          value === '__none__' ? '' : value,
                                      };
                                      return { ...current, credentials: next };
                                    })
                                  }
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue
                                      placeholder={gt('Select credential...', {
                                        context:
                                          'placeholder for credential selector in web browser entry',
                                      })}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">
                                      <T context="placeholder option in credential selector">
                                        — select —
                                      </T>
                                    </SelectItem>
                                    <PasswordCredentialOptions
                                      credentials={passwordCredentials}
                                    />
                                  </SelectContent>
                                </Select>
                                <Input
                                  className="h-8 text-xs"
                                  value={entry.label}
                                  onChange={(e) =>
                                    setWebBrowserConfig((current) => {
                                      const next = [...current.credentials];
                                      next[idx] = {
                                        ...next[idx],
                                        label: e.target.value,
                                      };
                                      return { ...current, credentials: next };
                                    })
                                  }
                                  placeholder={gt(
                                    'Label, e.g. "Google login"',
                                    {
                                      context:
                                        'placeholder for credential label in web browser entry',
                                    },
                                  )}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={gt('Remove credential entry', {
                                  context:
                                    'button label to remove a web browser credential row',
                                })}
                                className="h-7 w-7 text-muted-foreground hover:text-destructive flex-shrink-0 mt-0.5"
                                onClick={() =>
                                  setWebBrowserConfig((current) => ({
                                    ...current,
                                    credentials: current.credentials.filter(
                                      (_, i) => i !== idx,
                                    ),
                                  }))
                                }
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        <T context="help text for web browser credentials list">
                          Add saved credentials for websites the agent needs to
                          log into. The label helps the agent know which
                          credential to use where. Manage credentials in Org
                          Settings.
                        </T>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>
                        <T context="label for web browser MFA code source">
                          MFA code source
                        </T>
                      </Label>
                      <Select
                        value={webBrowserConfig.otpTargetId || '__none__'}
                        onValueChange={(value) =>
                          setWebBrowserConfig((current) => ({
                            ...current,
                            otpTargetId: value === '__none__' ? '' : value,
                          }))
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            <T context="OTP target dropdown placeholder option">
                              None — the portal has no MFA
                            </T>
                          </SelectItem>
                          {otpConfigs.map((target) => (
                            <SelectItem
                              key={target.targetId}
                              value={target.targetId}
                            >
                              {target.name}
                            </SelectItem>
                          ))}
                          {smsNumber ? (
                            <SelectItem value={smsNumber.otpTargetId}>
                              <T context="SMS OTP target option">
                                Text message to{' '}
                                <Var>{smsNumber.phoneNumber}</Var>
                              </T>
                            </SelectItem>
                          ) : null}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        <T context="help text for web browser MFA code source">
                          Pick the authenticator secret or texted-code number
                          registered for this portal and the operator can clear
                          its two-factor screen — it resolves a live code and
                          types it without ever seeing it. Manage these in Org
                          Settings under OTP.
                        </T>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="webBrowserTimeoutSeconds">
                        <T context="label for web browser turn timeout">
                          Turn timeout (seconds)
                        </T>
                      </Label>
                      <Input
                        id="webBrowserTimeoutSeconds"
                        type="number"
                        min={10}
                        max={3600}
                        step={30}
                        className="h-8 text-xs"
                        value={webBrowserConfig.timeoutSeconds ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const parsed = Number(raw);
                          setWebBrowserConfig((current) => ({
                            ...current,
                            timeoutSeconds:
                              raw === '' || !Number.isFinite(parsed)
                                ? null
                                : Math.max(
                                    10,
                                    Math.min(3600, Math.round(parsed)),
                                  ),
                          }));
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        <T context="help text for web browser turn timeout">
                          Wall-clock budget for one operator turn. Leave blank
                          for the 300s default; raise it when a login with an
                          MFA challenge or a slow download legitimately runs
                          longer.
                        </T>
                      </p>
                    </div>

                    <div className="space-y-3 rounded-md border p-2">
                      <div className="space-y-0.5">
                        <Label>
                          <T context="label for web browser input pacing section">
                            Input pacing
                          </T>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          <T context="help text for web browser input pacing section">
                            Defaults are instant (robotic). Raise these to mimic
                            a human for anti-bot sites.
                          </T>
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label
                          htmlFor="webBrowserTypingDelayMs"
                          className="text-xs"
                        >
                          <T context="label for typing delay input">
                            Typing delay (ms per keystroke)
                          </T>
                        </Label>
                        <Input
                          id="webBrowserTypingDelayMs"
                          type="number"
                          min={0}
                          max={1000}
                          step={10}
                          className="h-8 text-xs"
                          value={webBrowserConfig.typingDelayMs}
                          onChange={(e) => {
                            const parsed = Number(e.target.value);
                            setWebBrowserConfig((current) => ({
                              ...current,
                              typingDelayMs: Number.isFinite(parsed)
                                ? Math.max(
                                    0,
                                    Math.min(1000, Math.round(parsed)),
                                  )
                                : 0,
                            }));
                          }}
                        />
                        <p className="text-xs text-muted-foreground">
                          <T context="help text for typing delay input">
                            0 = automatic (instant in a browser; paced on a
                            remote desktop); ~40 = human-like.
                          </T>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="webBrowserMouseSmooth"
                          checked={webBrowserConfig.mouseSmooth}
                          onCheckedChange={(checked) =>
                            setWebBrowserConfig((current) => ({
                              ...current,
                              mouseSmooth: checked === true,
                            }))
                          }
                        />
                        <Label
                          htmlFor="webBrowserMouseSmooth"
                          className="cursor-pointer text-xs"
                        >
                          <T context="checkbox label for smooth mouse movement">
                            Smooth mouse movement
                          </T>
                        </Label>
                      </div>
                      {webBrowserConfig.mouseSmooth && (
                        <div className="space-y-1">
                          <Label
                            htmlFor="webBrowserMouseDurationMs"
                            className="text-xs"
                          >
                            <T context="label for mouse move duration input">
                              Mouse move duration (ms, optional)
                            </T>
                          </Label>
                          <Input
                            id="webBrowserMouseDurationMs"
                            type="number"
                            min={0}
                            max={5000}
                            step={50}
                            className="h-8 text-xs"
                            value={webBrowserConfig.mouseDurationMs ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const parsed = Number(raw);
                              setWebBrowserConfig((current) => ({
                                ...current,
                                mouseDurationMs:
                                  raw === '' || !Number.isFinite(parsed)
                                    ? null
                                    : Math.max(
                                        0,
                                        Math.min(5000, Math.round(parsed)),
                                      ),
                              }));
                            }}
                          />
                          <p className="text-xs text-muted-foreground">
                            <T context="help text for mouse move duration input">
                              Leave blank to let the provider time the move by
                              distance.
                            </T>
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      <Label>
                        <T context="label for web browser procedures pointer">
                          Procedures
                        </T>
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        <T context="help text pointing to the Procedures tab for procedure setup">
                          Manage the procedures this agent can use — and turn
                          them on — from the Procedures tab.
                        </T>
                      </p>
                    </div>

                    {userIsAdmin && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`webBrowserHeadless-${selectedTool.type}`}
                          checked={webBrowserConfig.headless}
                          onCheckedChange={(checked) =>
                            setWebBrowserConfig((current) => ({
                              ...current,
                              headless: checked === true,
                            }))
                          }
                        />
                        <Label
                          htmlFor={`webBrowserHeadless-${selectedTool.type}`}
                          className="cursor-pointer"
                        >
                          <T context="checkbox label run web browser headless">
                            Run headless
                          </T>
                        </Label>
                      </div>
                    )}
                  </div>
                )}

                {selectedTool.type === 'load_cash_receipt_work_item' && (
                  <CashReceiptKeyingConfigFields
                    value={getCashReceiptKeyingConfig(currentConfig)}
                    disabled={!isEditable}
                    onChange={(next) =>
                      updateConfig((config) => ({
                        ...config,
                        [CASH_RECEIPT_KEYING_CONFIG_KEY]: next,
                      }))
                    }
                  />
                )}

                {selectedTool.type !== 'login_to_remote_desktop' &&
                  selectedTool.type !== 'generate_session_summary' &&
                  selectedTool.type !== 'load_cash_receipt_work_item' &&
                  selectedTool.type !== 'remote_desktop_execute' &&
                  selectedTool.type !== 'web_browser_execute' && (
                    <p className="text-xs text-muted-foreground">
                      <T context="when agent tool has no config UI yet">
                        No configuration renderer is available for this tool
                        yet.
                      </T>
                    </p>
                  )}
              </div>
            </ScrollArea>
          </>
        ) : (
          <>
            {/* Catalog header with search */}
            <div className="p-3 border-b flex-shrink-0 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HammerIcon className="w-4 h-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold">
                    <T context="agent tool catalog panel title">Tool Catalog</T>
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  <T context="count of enabled tools vs catalog size">
                    <Var>{enabledTools.length}</Var> of{' '}
                    <Var>{catalogData?.tools?.length ?? 0}</Var> enabled
                  </T>
                </p>
              </div>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={gt('Search tools...', {
                    context: 'placeholder search agent tool catalog',
                  })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {deprecatedCount > 0 && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="show-deprecated-tools"
                    checked={showDeprecated}
                    onCheckedChange={(checked) =>
                      setShowDeprecated(checked === true)
                    }
                  />
                  <Label
                    htmlFor="show-deprecated-tools"
                    className="cursor-pointer text-xs text-muted-foreground"
                  >
                    <T context="checkbox to show deprecated tools in catalog">
                      Show deprecated (<Var>{deprecatedCount}</Var>)
                    </T>
                  </Label>
                </div>
              )}
            </div>

            {/* Catalog list */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2">
                <Accordion
                  type="multiple"
                  defaultValue={Array.from(groupedTools.keys())}
                >
                  {Array.from(groupedTools.entries()).map(
                    ([category, tools]) => (
                      <AccordionItem key={category} value={category}>
                        <AccordionTrigger>
                          <div className="flex items-center gap-2">
                            <span>{category}</span>
                            <Badge variant="secondary" className="text-xs">
                              {
                                tools.filter((t) =>
                                  enabledTools.includes(t.type),
                                ).length
                              }
                              /{tools.length}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-1.5 pt-1">
                            {tools.map((tool) => {
                              const isEnabled = enabledTools.includes(
                                tool.type,
                              );
                              // Lifecycle-managed tools are owned by the task
                              // loop when task management is enabled — disabled
                              // here and stripped at runtime.
                              const isLifecycleManaged =
                                taskManagementEnabled &&
                                tool.lifecycleManaged === true;
                              const isDisabled =
                                !isEditable ||
                                tool.visibility?.visibility === 'disabled' ||
                                isLifecycleManaged;
                              const permissionCount =
                                tool.requiredPermissions?.length ?? 0;

                              const toolCard = (
                                <div
                                  key={tool.type}
                                  className={cn(
                                    'flex items-start gap-2.5 rounded-md border p-2 transition-colors',
                                    isDisabled
                                      ? 'opacity-50 cursor-not-allowed bg-muted/30'
                                      : [
                                          'cursor-pointer',
                                          isEnabled
                                            ? 'border-primary/30 bg-primary/5'
                                            : 'hover:bg-muted/50',
                                        ],
                                  )}
                                  onClick={
                                    isDisabled
                                      ? undefined
                                      : () => setActiveToolType(tool.type)
                                  }
                                >
                                  <Checkbox
                                    checked={isEnabled}
                                    disabled={isDisabled}
                                    onCheckedChange={
                                      isDisabled
                                        ? undefined
                                        : () => toggleTool(tool.type)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0 space-y-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className={cn(
                                          'font-medium text-sm',
                                          isDisabled && 'text-muted-foreground',
                                          !isDisabled &&
                                            tool.deprecated &&
                                            'line-through text-muted-foreground',
                                        )}
                                      >
                                        {tool.name}
                                      </span>
                                      {isDisabled && (
                                        <ProhibitIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                      )}
                                      {!isDisabled && tool.deprecated && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] px-1 py-0 text-muted-foreground"
                                            >
                                              <T context="badge for deprecated agent tool">
                                                Deprecated
                                              </T>
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {tool.deprecated.reason}
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                      {!isDisabled && tool.readOnly && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <EyeIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <T context="tooltip catalog tool is read-only">
                                              Read-only
                                            </T>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                      {!isDisabled && permissionCount > 0 && (
                                        <PermissionListPopover
                                          permissions={
                                            tool.requiredPermissions ?? []
                                          }
                                        >
                                          <span className="inline-flex items-center gap-0.5 text-muted-foreground flex-shrink-0 cursor-pointer">
                                            <ShieldCheckIcon className="h-3.5 w-3.5" />
                                            <span className="text-[10px]">
                                              {permissionCount}
                                            </span>
                                          </span>
                                        </PermissionListPopover>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {tool.description}
                                    </p>
                                  </div>
                                </div>
                              );

                              if (isDisabled) {
                                return (
                                  <Tooltip key={tool.type}>
                                    <TooltipTrigger asChild>
                                      <div>{toolCard}</div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      {/* Read-only first: when the version isn't an editable draft
                                          every tool is disabled, and the admin/permission wording
                                          below would name the wrong reason. */}
                                      {!isEditable ? (
                                        <T context="tooltip when agent tools are read-only because the selected version is not an editable draft">
                                          This version is read-only. Choose Edit
                                          to start a draft before changing its
                                          tools.
                                        </T>
                                      ) : isLifecycleManaged ? (
                                        <T context="tooltip when an agent tool is managed by the task loop">
                                          Managed by the task loop. This agent
                                          reports task status through its
                                          outcome, so the loop handles status
                                          changes and hand-offs — turn off task
                                          management to use this tool directly.
                                        </T>
                                      ) : tool.visibility?.disabledReason?.trim() ? (
                                        tool.visibility.disabledReason
                                      ) : (
                                        <T context="tooltip when agent tool is disabled for non-admin users">
                                          Please contact your administrator to
                                          enable this feature.
                                        </T>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              }

                              return toolCard;
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ),
                  )}
                </Accordion>

                {groupedTools.size === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {search ? (
                      <T context="empty state no tools match search in catalog">
                        No tools match your search.
                      </T>
                    ) : (
                      <T context="empty state agent tool catalog has no tools">
                        No tools available in the catalog.
                      </T>
                    )}
                  </p>
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </div>
    </fieldset>
  );
}
